import "./index.css";
import * as React from "react";
import { BannerFormRegistration } from "../FormRegistration/components/Banner";
import { StepperFormRegistration } from "../FormRegistration/components/Stepper";
import Show from "../FormRegistration/components/Show";
import classNames from "classnames";
import useWindowSize from "../../../hooks/useWindowSize";
import { ConfirmationRegistrationForm } from "../FormRegistration/components/Confirmation";
import useToggle from "../../../hooks/useToggle";
import { EventDetailProps, useRegistrationFormDance, useUserData } from "../../../store/user";
import {
  useFetchCurrentRegistrationData,
  useFetchEventDetail,
  usePostRegistrationDanceData,
  usePostUpdateDanceRegistrationData,
} from "../../../networks";
import { useNavigate, useParams } from "react-router-dom";
import { getStringSafely, SCHOOL_CATEGORIES } from "../FormRegistration/utils";
import WarningBannerFormRegistration from "../FormRegistration/components/WarningBanner/WarningBanner";
import Swal from "sweetalert2";
import StepDataAkun from "./form/DataAkun";
import StepDataSekolah from "./form/DataSekolah";
import StepDataTeam from "./form/DataTeam";

const imgPath = `${process.env.REACT_APP_API_HOST}/uploads/featured-images/`;
const imgRegisPath = `${process.env.REACT_APP_API_HOST}uploads/registration/`;

const FormRegistrationDance = () => {
  const navigate = useNavigate();
  const formRef = React.useRef<HTMLFormElement>(null);

  const { size } = useWindowSize();
  const { city, category, region_id, mode, category_id } = useParams();
  // @ts-ignore
  const isDesktopView = (size?.width || 0) >= 768;
  const isEditMode = mode === "edit";

  const { setAccessToken, setEmail, email: userEmail } = useUserData();

  const [step, setStep] = React.useState<number>(0);

  const { mutateAsync: postRegistrationData, isLoading: loadingRegistrationData } =
    usePostRegistrationDanceData();
  const { mutateAsync: postUpdateRegistrationData, isLoading: loadingUpdateRegistrationData } =
    usePostUpdateDanceRegistrationData();
  const { data: dataDetailRaw, isFetching: fetchingDetailRaw } = useFetchEventDetail(city);
  const { data: registrationDataRaw, isFetching: fetchingRegistrationData } =
    useFetchCurrentRegistrationData(isEditMode);

  const registrationData = registrationDataRaw?.data?.data?.registration;
  const revisionData = registrationData?.registration_revision ?? "";
  const isRegistrationNeedRevision =
    isEditMode && registrationData?.registration_status === "Need Revision" && !!registrationData;

  /**  TODO: add submit loading  */
  const isLoadingButton =
    fetchingDetailRaw ||
    loadingRegistrationData ||
    fetchingRegistrationData ||
    loadingUpdateRegistrationData;

  const dataDetail: EventDetailProps = dataDetailRaw?.data?.data?.eventDetail;

  const { dataAccount, dataSchool, dataTeam, setDataAccount, setDataSchool, setDataTeam } =
    useRegistrationFormDance();

  const { active: visibleConfirmation, toggleActive: toggleVisibleConfirmation } = useToggle();

  const handleSubmitData = async () => {
    let formData = new FormData();

    /** SUBMIT DATA */
    formData.append("region_id", String(region_id));
    formData.append("school_category", String(category).toUpperCase());
    formData.append("event_category_id", String(category_id));

    /* Data Account  */
    if (!isEditMode) {
      formData.append("email", getStringSafely(dataAccount?.email));
      formData.append("password", getStringSafely(dataAccount?.password));
    }
    formData.append("password_confirmation", getStringSafely(dataAccount?.password));

    /** Data Sekolah  */
    formData.append("school_name", getStringSafely(dataSchool?.school_name));
    formData.append("school_address", getStringSafely(dataSchool?.school_address));
    formData.append("npsn", getStringSafely(dataSchool?.npsn_number));
    formData.append("school_coach_name", getStringSafely(dataSchool?.contact_person_name));
    formData.append(
      "school_coach_phone_number",
      getStringSafely(dataSchool?.contact_person_number),
    );
    formData.append("team_instagram", getStringSafely(dataSchool?.team_social_media));
    formData.append("video_url", getStringSafely(dataSchool?.video_url));

    /** Data Tim Dance */
    for (let i = 1; i <= 10; i++) {
      const playerNameKey = `name_team_member_${i}`;
      const playerPhoneKey = `number_team_member_${i}`;

      if (!!dataTeam?.[playerNameKey]) {
        formData.append(`players[${i - 1}][player_name]`, getStringSafely(dataTeam[playerNameKey]));
      }
      if (!!dataTeam?.[playerPhoneKey]) {
        formData.append(
          `players[${i - 1}][player_phone]`,
          getStringSafely(dataTeam[playerPhoneKey]),
        );
      }
    }

    if (isEditMode) {
      return handleUpdateRegistrationData(formData);
    }

    handleRegistrationData(formData);
  };

  const handleRegistrationData = async (formData: FormData) => {
    if (!SCHOOL_CATEGORIES?.includes(category?.toLowerCase() ?? "")) {
      return Swal.fire({
        title: "Oops! Something went wrong!",
        text: "Data kategori sekolah belum sesuai",
        icon: "error",
      });
    }

    try {
      const postRegistration = await postRegistrationData(formData);
      if (postRegistration) {
        setAccessToken(postRegistration?.data?.access_token);
        setEmail(dataAccount?.email ?? "");
        navigate(`/${city}/registrasi-success`, {
          state: {
            action: "registrasi",
          },
        });
      }
    } catch (error) {
      toggleVisibleConfirmation();

      const errorData = error?.response?.data?.errors || error?.response?.data?.error;
      const errorMsg = error?.response?.data?.errors?.message || error?.response?.data?.message;

      if (errorData) {
        Swal.fire({
          title: "Oops! Something went wrong!",
          text: errorMsg || errorData,
          icon: "error",
        });

        return;
      }

      Swal.fire({
        title: "Oops! Something went wrong!",
        text: "Registration failed!",
        icon: "error",
      });
    }
  };

  const handleUpdateRegistrationData = async (formData: FormData) => {
    if (!SCHOOL_CATEGORIES?.includes(category?.toLowerCase() ?? "")) {
      return Swal.fire({
        title: "Oops! Something went wrong!",
        text: "Data kategori sekolah belum sesuai",
        icon: "error",
      });
    }

    try {
      const postData = await postUpdateRegistrationData(formData);

      if (!!postData) {
        navigate(`/${city}/registrasi-success`, {
          state: {
            action: "update data pendaftaran",
          },
        });
      }
    } catch (error) {
      toggleVisibleConfirmation();
      const errorData = error?.response?.data?.errors || error?.response?.data?.error;
      const errorMsg = error?.response?.data?.errors?.message || error?.response?.data?.message;

      if (errorData) {
        Swal.fire({
          title: "Oops! Something went wrong!",
          text: errorMsg || errorData,
          icon: "error",
        });

        return;
      }

      Swal.fire({
        title: "Oops! Something went wrong!",
        text: "Registration update data failed!",
        icon: "error",
      });
    }
  };

  const prefilledData = React.useCallback(() => {
    if (isEditMode && registrationData) {
      /** TODO: prefilled data  */
      setDataAccount({
        email: userEmail,
      });

      setDataSchool({
        school_name: getStringSafely(registrationData?.school_name),
        school_address: getStringSafely(registrationData?.school_address),
        npsn_number: getStringSafely(registrationData?.npsn),
        team_social_media: getStringSafely(registrationData?.team_instagram),
        contact_person_name: getStringSafely(registrationData?.school_coach_name),
        contact_person_number: getStringSafely(registrationData?.school_coach_phone_number),
        video_url: getStringSafely(registrationData?.video_url),
      });

      let dancerList = {};
      const dataDancer = registrationData?.players ?? [];

      Array(10)
        ?.fill(null)
        ?.forEach((_, idx) => {
          const nameKey = `name_team_member_${idx + 1}`;
          const phoneKey = `number_team_member_${idx + 1}`;

          dancerList[nameKey] = dataDancer?.[idx]?.player_name;
          dancerList[phoneKey] = dataDancer?.[idx]?.player_phone;
        });

      setDataTeam(dancerList);
    }
  }, [isEditMode, registrationData]);

  const validateParamsData = () => {
    const isValidCategory = SCHOOL_CATEGORIES?.includes(category?.toLowerCase() ?? "");
    const isValidRegionId = region_id && region_id !== "0";
    const isValidEventCategoryId = category_id && category_id !== "0";

    return isValidCategory && isValidRegionId && isValidEventCategoryId;
  };

  React.useEffect(() => {
    prefilledData();
  }, [prefilledData]);

  // Prevent on submit form
  React.useEffect(() => {
    if (formRef.current) {
      formRef.current.addEventListener("submit", (event) => event.preventDefault());
    }
  }, [formRef]);

  React.useEffect(() => {
    const validPath = validateParamsData();
    if (!validPath) return navigate("/registrasi");

    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = "auto";
    };
  }, []);

  return (
    <div
      style={{ height: "100vh" }}
      className={classNames("mx-auto overflow-y-hidden bg-white w-full max-w-[600px]", {
        "border-[1px] border-[#F0F2F9] rounded-[16px] pb-[20px]": isDesktopView,
      })}
    >
      <div className="h-[66px]" />
      <BannerFormRegistration
        data={{
          city: dataDetail?.city,
          date: dataDetail?.event_date,
          iconUrl: `${imgPath}${
            isDesktopView ? dataDetail?.key_visual : dataDetail?.key_visual_mobile
          }`,
          eventPlace: dataDetail?.location,
        }}
      />
      <div style={{ height: "calc(100% - 150px)" }} className="overflow-y-auto pb-[20px]">
        {/* Alert Section */}
        <Show when={isRegistrationNeedRevision}>
          <WarningBannerFormRegistration revision={revisionData} />
        </Show>
        <StepperFormRegistration
          data={[
            {
              key: "index-1",
              title: "Akun",
            },
            {
              key: "index-2",
              title: "Data Sekolah",
            },
            {
              key: "index-3",
              title: "Data Tim",
            },
          ]}
          currentStep={step}
        />

        <form ref={formRef} encType="multipart/form-data">
          <Show when={step === 0}>
            <StepDataAkun isLoading={isLoadingButton} setStep={setStep} />
          </Show>
          <Show when={step === 1}>
            <StepDataSekolah isLoading={isLoadingButton} setStep={setStep} />
          </Show>
          <Show when={step === 2}>
            <StepDataTeam
              isLoading={isLoadingButton}
              setStep={setStep}
              onOpenConfirmation={toggleVisibleConfirmation}
            />
          </Show>
        </form>
      </div>

      <ConfirmationRegistrationForm
        isLoading={isLoadingButton}
        open={visibleConfirmation}
        onClose={toggleVisibleConfirmation}
        onSubmitData={handleSubmitData}
      />
    </div>
  );
};

export default FormRegistrationDance;
